import { createHash } from "node:crypto";
import { Command, Option } from "clipanion";
import { AzureCliCredential } from "@azure/identity";
import type { TokenCredential } from "@azure/identity";
import { TableClient } from "@azure/data-tables";
import { AIProjectClient } from "@azure/ai-projects";
import { fetchLedgerRows, type LedgerRow } from "@m8t-stack/agent-ledger/read";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { getAzAccount } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { discoverLedgerResources } from "../../lib/dream-discovery.js";

/** The AgentLedger Table Storage table (PK = lowercase agentName). */
const LEDGER_TABLE_NAME = "AgentLedger";

/** Public-visitor conversations live 30 days (the end-user-key cookie). */
const KEY_LIFETIME_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The conversation-registry owner digest: `ownerKey` metadata is
 * `<platform>:<sha256-base64url(nativeUserKey)>` (see the webapp's
 * active-conversation registry). Recomputed here so a candidate can be
 * POSITIVELY bound to the delegated identity its ledger rows attribute it to —
 * any drift in the scheme makes the match fail, which skips the conversation
 * rather than deleting it.
 */
export function ownerDigest(nativeUserKey: string): string {
  return createHash("sha256").update(nativeUserKey).digest("base64url");
}

/** What the sweep knows about one delegated-visitor conversation from the ledger. */
export interface SvcConversationFact {
  conversationId: string;
  /** The delegated end-user identity (`svc:<principal>:<key>`) — the conversation's nativeUserKey. */
  svcRef: string;
  /** The service principal that asserted the end user (second `svc:` segment). */
  principal: string;
  agent: string;
  /** Latest ledger activity observed in the scanned window (ISO). */
  lastSeen: string;
}

/** `svc:<principalOid>:<key>` → the principal segment ("" when malformed). */
export function svcPrincipal(svcRef: string): string {
  return svcRef.split(":")[1] ?? "";
}

/**
 * Collapse ledger rows to per-conversation facts, keeping ONLY conversations
 * owned by a delegated end-user (`svc:`-shaped userRef). Team, channel, a2a,
 * and unknown-owner rows never produce a fact — they are structurally outside
 * the sweep.
 */
export function svcConversationFacts(rows: LedgerRow[]): Map<string, SvcConversationFact> {
  const facts = new Map<string, SvcConversationFact>();
  for (const r of rows) {
    if (!r.foundryConversationId) continue;
    if (!r.userRef?.startsWith("svc:")) continue;
    const prev = facts.get(r.foundryConversationId);
    if (!prev || r.eventTimestamp > prev.lastSeen) {
      facts.set(r.foundryConversationId, {
        conversationId: r.foundryConversationId,
        svcRef: r.userRef,
        principal: svcPrincipal(r.userRef),
        agent: r.agentName,
        lastSeen: r.eventTimestamp,
      });
    }
  }
  return facts;
}

/** Facts whose last observed activity is strictly before the cutoff, oldest first. */
export function selectStaleCandidates(
  facts: Map<string, SvcConversationFact>,
  cutoffIso: string,
): SvcConversationFact[] {
  return [...facts.values()]
    .filter((f) => f.lastSeen < cutoffIso)
    .sort((a, b) => (a.lastSeen < b.lastSeen ? -1 : 1));
}

export interface SweepConversationMeta {
  app?: string;
  ownerKey?: string;
  platform?: string;
}

/**
 * The delete-time confirmation. Every condition fails CLOSED — a conversation
 * is deleted only when it is provably the ledger-attributed visitor's own
 * thread AND provably inactive by its newest item:
 * - metadata must be readable, tagged `app: m8t-webapp` (delegated callers
 *   ride the web conversation service), and its ownerKey must equal
 *   `web:<digest(svcRef)>` — the exact owner the ledger attributes;
 * - the newest conversation item must exist and predate the cutoff (`null`
 *   items = unprovable, skip).
 */
export function confirmCandidate(
  fact: SvcConversationFact,
  meta: SweepConversationMeta,
  newestItemAt: string | null,
  cutoffIso: string,
): { ok: true } | { ok: false; reason: string } {
  if (meta.app !== "m8t-webapp") return { ok: false, reason: `unexpected app tag '${meta.app ?? ""}'` };
  const expectedOwner = `web:${ownerDigest(fact.svcRef)}`;
  if (meta.ownerKey !== expectedOwner) return { ok: false, reason: "owner mismatch" };
  if (newestItemAt === null) return { ok: false, reason: "no items to prove inactivity" };
  if (newestItemAt >= cutoffIso) return { ok: false, reason: "recent activity" };
  return { ok: true };
}

export interface SweepContext {
  projectEndpoint: string;
  ledgerTableEndpoint: string;
}

/**
 * Resolution seam. The command builds concrete deps from `AzureCliCredential`
 * + ARM discovery at the edge (`defaultDeps`); unit tests assign `cmd.deps`
 * directly with in-memory fakes so no live Azure is touched.
 */
export interface SweepDeps {
  resolveContext(opts: { subscription?: string; endpoint?: string; storageAccount?: string }): Promise<SweepContext>;
  /** Ledger rows across ALL workers in [fromIso, toIso]. */
  fetchRows(ctx: SweepContext, fromIso: string, toIso: string): Promise<LedgerRow[]>;
  /** Conversation metadata; "gone" on 404. Infrastructure failures (auth,
   *  throttling, outage) THROW — they must fail the run, not soften to skips. */
  retrieveMetadata(ctx: SweepContext, conversationId: string): Promise<SweepConversationMeta | "gone">;
  /** Newest item's ISO timestamp (`at`); null = no items; "gone" on 404. Infrastructure failures THROW. */
  newestItemAt(ctx: SweepContext, conversationId: string): Promise<{ at: string | null } | "gone">;
  deleteConversation(ctx: SweepContext, conversationId: string): Promise<"deleted" | "already-gone">;
}

function statusOf(e: unknown): number | undefined {
  const err = e as { statusCode?: number; status?: number } | null | undefined;
  return err?.statusCode ?? err?.status;
}

function defaultDeps(): SweepDeps {
  const credential: TokenCredential = new AzureCliCredential();
  const openaiFor = (ctx: SweepContext) =>
    new AIProjectClient(ctx.projectEndpoint, credential).getOpenAIClient();
  return {
    async resolveContext(opts) {
      const account = await getAzAccount();
      const subscriptionId = opts.subscription ?? account.subscriptionId;
      const project = await resolveFoundryProject({
        credential,
        subscriptionId,
        interactive: false,
        endpoint: opts.endpoint,
      });
      // …/resourceGroups/<rg>/… (same parse the dream context applies).
      const m = /\/resourceGroups\/([^/]+)/i.exec(project.accountScope);
      if (!m) throw new LocalCliError({ code: "sweep_context", message: `Could not parse resource group from '${project.accountScope}'.` });
      const { ledgerTableEndpoint } = await discoverLedgerResources({
        credential,
        subscriptionId,
        resourceGroup: m[1],
        storageAccount: opts.storageAccount,
      });
      return { projectEndpoint: project.endpoint, ledgerTableEndpoint };
    },
    async fetchRows(ctx, fromIso, toIso) {
      const client = new TableClient(ctx.ledgerTableEndpoint, LEDGER_TABLE_NAME, credential as never);
      try {
        return await fetchLedgerRows({ workers: [], source: "all", from: fromIso, to: toIso }, client);
      } catch (e) {
        // Managing a storage account and reading its rows are separate grants,
        // so an operator with Owner still gets a 403 here. Name the missing
        // role rather than surfacing the raw OData body.
        const msg = e instanceof Error ? e.message : String(e);
        if (statusOf(e) === 403 || msg.includes("AuthorizationPermissionMismatch")) {
          const account = /https:\/\/([^.]+)\./.exec(ctx.ledgerTableEndpoint)?.[1] ?? "the ledger account";
          throw new LocalCliError({
            code: "sweep_ledger_forbidden",
            message: `Not authorized to read the ledger table on '${account}'.`,
            hint: `Reading table rows needs a data-plane role: grant 'Storage Table Data Reader' on that storage account to the signed-in identity (owning the account is not enough).`,
            cause: e,
          });
        }
        throw e;
      }
    },
    async retrieveMetadata(ctx, conversationId) {
      try {
        const conv = await openaiFor(ctx).conversations.retrieve(conversationId);
        const md = (conv as { metadata?: Record<string, unknown> }).metadata;
        const s = (v: unknown) => (typeof v === "string" ? v : undefined);
        if (!md || typeof md !== "object") return {};
        return { app: s(md.app), ownerKey: s(md.ownerKey), platform: s(md.platform) };
      } catch (e) {
        if (statusOf(e) === 404) return "gone";
        throw e;
      }
    },
    async newestItemAt(ctx, conversationId) {
      try {
        // items.list returns NEWEST-first; the first item dates the latest activity.
        for await (const item of openaiFor(ctx).conversations.items.list(conversationId)) {
          const created = (item as { created_at?: number }).created_at;
          if (typeof created !== "number") return { at: null };
          return { at: new Date(created * 1000).toISOString() };
        }
        return { at: null };
      } catch (e) {
        if (statusOf(e) === 404) return "gone";
        throw e;
      }
    },
    async deleteConversation(ctx, conversationId) {
      try {
        await openaiFor(ctx).conversations.delete(conversationId);
        return "deleted";
      } catch (e) {
        if (statusOf(e) === 404) return "already-gone";
        throw e;
      }
    },
  };
}

export class ConversationsSweepCommand extends M8tCommand {
  static paths = [["conversations", "sweep"]];
  static usage = Command.Usage({
    category: "Conversations",
    description: "Delete expired public-visitor conversations (dry run by default)",
    details: `
      Finds conversations owned by delegated end users (public visitors) whose
      last activity predates the retention window (${String(KEY_LIFETIME_DAYS)}-day key life + grace),
      confirms each against its own metadata and newest item, and — only with
      --delete — permanently removes them. Team, channel, and delegated-work
      conversations are structurally out of scope. The masked ledger telemetry
      of swept conversations is kept.
    `,
    examples: [
      ["Report what would be deleted (nothing is)", "m8t conversations sweep"],
      ["Delete, capped at 50", "m8t conversations sweep --delete --max 50"],
    ],
  });

  doDelete = Option.Boolean("--delete", false, {
    description: "Perform deletions (without this flag the command only reports)",
  });
  principal = Option.String("--principal", {
    description:
      "Service-principal oid whose end-user keys are known-ephemeral (required with --delete; a dry run without it reports candidates grouped by principal)",
  });
  max = Option.String("--max", "200", { description: "Maximum deletions per run" });
  graceDays = Option.String("--grace-days", "14", {
    description: "Days past the 30-day key life before a conversation is eligible",
  });
  lookbackDays = Option.String("--lookback-days", "180", {
    description: "How far back to scan ledger activity for candidates",
  });
  subscription = Option.String("--subscription", { description: "Azure subscription id override" });
  endpoint = Option.String("--endpoint", { description: "Foundry project endpoint override" });
  storageAccount = Option.String("--storage-account", {
    description: "Ledger storage account name (skips tag-based discovery)",
  });

  deps: SweepDeps = defaultDeps();

  async executeCommand(): Promise<number> {
    const out = this.context.stdout;
    const max = Number.parseInt(this.max, 10);
    const grace = Number.parseInt(this.graceDays, 10);
    const lookback = Number.parseInt(this.lookbackDays, 10);
    if (!Number.isFinite(max) || max < 1) throw new LocalCliError({ code: "sweep_args", message: "--max must be a positive integer." });
    if (!Number.isFinite(grace) || grace < 0) throw new LocalCliError({ code: "sweep_args", message: "--grace-days must be a non-negative integer." });
    const retentionDays = KEY_LIFETIME_DAYS + grace;
    if (!Number.isFinite(lookback) || lookback <= retentionDays) {
      throw new LocalCliError({ code: "sweep_args", message: `--lookback-days must exceed the retention window (${String(retentionDays)} days).` });
    }

    const now = Date.now();
    const cutoffIso = new Date(now - retentionDays * DAY_MS).toISOString();
    const fromIso = new Date(now - lookback * DAY_MS).toISOString();
    const toIso = new Date(now).toISOString();

    const ctx = await this.deps.resolveContext({
      subscription: this.subscription,
      endpoint: this.endpoint,
      storageAccount: this.storageAccount,
    });
    out.write(`Scanning ledger activity ${fromIso} \u2192 ${toIso}; retention cutoff ${cutoffIso}.\n`);

    const rows = await this.deps.fetchRows(ctx, fromIso, toIso);
    const facts = svcConversationFacts(rows);
    const stale = selectStaleCandidates(facts, cutoffIso);

    // `svc:` proves a service principal asserted an end user \u2014 it does NOT
    // prove the key expires (the platform contract allows stable integration
    // identities). Deletion therefore requires the operator to NAME the
    // principal whose keys are known-ephemeral (the public-visitor minter);
    // every other principal's conversations are structurally out of scope.
    if (this.doDelete && !this.principal) {
      throw new LocalCliError({
        code: "sweep_principal_required",
        message: "--delete requires --principal <service-principal oid>.",
        hint: "Run without --delete first: the dry run lists candidates grouped by principal so you can identify the visitor-minting one.",
      });
    }
    const inScope = this.principal ? stale.filter((f) => f.principal === this.principal) : stale;
    const outOfScope = stale.length - inScope.length;

    out.write(
      `${String(facts.size)} delegated conversation(s) seen in window; ${String(stale.length)} past cutoff` +
        (this.principal ? ` (${String(inScope.length)} for principal ${this.principal}, ${String(outOfScope)} other-principal ignored)` : "") +
        ".\n",
    );
    if (!this.principal && stale.length > 0) {
      const byPrincipal = new Map<string, number>();
      for (const f of stale) byPrincipal.set(f.principal, (byPrincipal.get(f.principal) ?? 0) + 1);
      out.write("Stale candidates by principal (pass --principal to scope deletion):\n");
      for (const [pr, n] of [...byPrincipal.entries()].sort((a, b) => b[1] - a[1])) {
        out.write(`  ${pr || "(malformed)"}  ${String(n)}\n`);
      }
    }
    out.write("\n");

    // The cap bounds confirmed (would-)deletions, not raw candidates \u2014 a
    // skipped or already-gone row must not consume a slot, or deleted rows
    // (whose ledger telemetry deliberately outlives them) would pin the cap
    // forever and later candidates would never be reached.
    let attempts = 0;
    let deleted = 0;
    let alreadyGone = 0;
    let skipped = 0;
    let failures = 0;
    let remaining = 0;
    for (const fact of inScope) {
      if (attempts >= max) {
        remaining += 1;
        continue;
      }
      const ageDays = Math.floor((now - Date.parse(fact.lastSeen)) / DAY_MS);
      const label = `${fact.conversationId}  agent=${fact.agent}  last-active=${fact.lastSeen} (${String(ageDays)}d)`;
      let meta: SweepConversationMeta | "gone";
      let newest: { at: string | null } | "gone";
      try {
        meta = await this.deps.retrieveMetadata(ctx, fact.conversationId);
        newest = meta === "gone" ? "gone" : await this.deps.newestItemAt(ctx, fact.conversationId);
      } catch (e) {
        failures += 1;
        out.write(`${colors.error("failed")}  ${label}  \u2014 ${(e as Error).message}\n`);
        continue;
      }
      if (meta === "gone" || newest === "gone") {
        alreadyGone += 1;
        out.write(`${colors.dim("already gone")}  ${label}\n`);
        continue;
      }
      const verdict = confirmCandidate(fact, meta, newest.at, cutoffIso);
      if (!verdict.ok) {
        skipped += 1;
        out.write(`${colors.warn("skip")}    ${label}  \u2014 ${verdict.reason}\n`);
        continue;
      }
      attempts += 1;
      if (!this.doDelete) {
        out.write(`${colors.field("would delete")}  ${label}\n`);
        continue;
      }
      try {
        const result = await this.deps.deleteConversation(ctx, fact.conversationId);
        if (result === "deleted") {
          deleted += 1;
          out.write(`${colors.error("deleted")} ${label}\n`);
        } else {
          alreadyGone += 1;
          out.write(`${colors.dim("already gone")}  ${label}\n`);
        }
      } catch (e) {
        failures += 1;
        out.write(`${colors.error("failed")}  ${label}  \u2014 ${(e as Error).message}\n`);
      }
    }

    out.write("\n");
    const tail =
      `already gone ${String(alreadyGone)}, skipped ${String(skipped)} (fail-closed), failed ${String(failures)}` +
      (remaining > 0 ? `, ${String(remaining)} past the cap \u2014 re-run to continue` : "") +
      ".\n";
    if (this.doDelete) {
      out.write(`Deleted ${String(deleted)}, ${tail}`);
    } else {
      out.write(`DRY RUN \u2014 nothing deleted. ${String(attempts)} conversation(s) would be deleted, ${tail}`);
    }
    if (failures > 0) {
      out.write(`${colors.error("Run incomplete:")} ${String(failures)} verification/deletion failure(s) \u2014 fix access and re-run.\n`);
      return 1;
    }
    return 0;
  }
}
